import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, Lock, Plus, Trash2, Users, Pause, Play, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const EMPTY = { label: "", dest: "watch", utm_source: "", utm_medium: "", utm_campaign: "", utm_content: "" };
const field = "mt-1 text-sm";

function Stat({ n, label, tid }) {
  return (
    <span className="text-center" data-testid={tid}>
      <span className="block text-sm font-bold tabular-nums" style={{ color: "#0F1226" }}>{n}</span>
      <span className="block text-[10px] uppercase tracking-wide text-ink-3">{label}</span>
    </span>
  );
}

/**
 * UTM links — the marketing manager's tab.
 * They name a campaign, tag it and hand out a short link. The wallet it credits
 * is resolved server-side from the rotation's top priority, so it never appears
 * here and can't be swapped.
 */
export default function UtmLinksTab() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState({ links: [], dests: [], locked: {} });
  const [form, setForm] = useState(EMPTY);
  const [openLeads, setOpenLeads] = useState(null);
  const [leads, setLeads] = useState({});

  // Always build the shareable URL from the browser's own origin.
  const fullUrl = (l) => `${window.location.origin}${l.path || ""}`;

  const load = async () => {
    try {
      const { data: d } = await api.get("/utm-links");
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load your links");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.label.trim()) { toast.error("Give the link a name"); return; }
    setBusy(true);
    try {
      await api.post("/utm-links", form);
      toast.success("Link created — copy it and start sharing");
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create the link");
    } finally { setBusy(false); }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the text and use Ctrl+C"); }
  };

  const toggle = async (l) => {
    try {
      await api.post(`/utm-links/${l.id}/toggle`, { active: !l.active });
      load();
    } catch { toast.error("Couldn't update the link"); }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.label}"? Anyone who still has the link will see a dead page.`)) return;
    try { await api.delete(`/utm-links/${l.id}`); toast.success("Link deleted"); load(); }
    catch { toast.error("Couldn't delete the link"); }
  };

  const showLeads = async (l) => {
    if (openLeads === l.id) { setOpenLeads(null); return; }
    setOpenLeads(l.id);
    if (leads[l.id]) return;
    try {
      const { data: d } = await api.get(`/utm-links/${l.id}/leads`);
      setLeads((p) => ({ ...p, [l.id]: d.leads || [] }));
    } catch { toast.error("Couldn't load the leads for this link"); }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-5" data-testid="share-content-utm">
      <div>
        <h3 className="font-display text-lg font-semibold tracking-tight">UTM links</h3>
        <p className="text-xs text-ink-3 mt-1 leading-relaxed">
          Build a tagged link per campaign, influencer or placement, then watch who came through it.
          Tag it however you like — the tags travel with the link, so your reports tell you exactly where a lead came from.
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-xl px-4 py-3.5"
        style={{ background: "rgba(14,165,233,0.06)", border: "1px dashed rgba(14,165,233,0.35)" }}
        data-testid="utm-locked-note">
        <Lock size={16} className="shrink-0 mt-0.5" style={{ color: "#0EA5E9" }} />
        <p className="text-xs leading-relaxed" style={{ color: "#0F1226" }}>
          <b>The destination wallet is locked.</b> Every link you build here credits the company&apos;s priority
          wallet, set by the admin — it isn&apos;t part of the link you copy, so it can&apos;t be changed or swapped.
          You only choose the landing page and the tags.
          {data.locked?.ready === false && (
            <span className="block mt-1 text-amber-700">
              Heads up: the admin hasn&apos;t set the priority wallet yet — links will still be created, but ask them to set it before you share.
            </span>
          )}
        </p>
      </div>

      {/* Builder */}
      <div className="rounded-xl p-4 space-y-3" style={{ background: "rgba(15,18,38,0.03)", border: "1px solid rgba(15,18,38,0.08)" }}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] font-medium text-ink-2">Link name <span className="text-ink-3">(just for you)</span></label>
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="Jan KOL push — @cryptokate" className={field} data-testid="utm-create-label" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-2">Where it lands</label>
            <select value={form.dest} onChange={(e) => setForm({ ...form, dest: e.target.value })}
              className="mt-1 w-full h-9 rounded-md border px-3 text-sm bg-white"
              style={{ borderColor: "rgba(15,18,38,0.15)" }} data-testid="utm-create-dest">
              {(data.dests || []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-2">Source <span className="text-ink-3">(where it&apos;s posted)</span></label>
            <Input value={form.utm_source} onChange={(e) => setForm({ ...form, utm_source: e.target.value })}
              placeholder="instagram" className={field} data-testid="utm-create-source" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-2">Medium <span className="text-ink-3">(how)</span></label>
            <Input value={form.utm_medium} onChange={(e) => setForm({ ...form, utm_medium: e.target.value })}
              placeholder="story / dm / paid" className={field} data-testid="utm-create-medium" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-ink-2">Campaign</label>
            <Input value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })}
              placeholder="jan-kol" className={field} data-testid="utm-create-campaign" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-medium text-ink-2">Content <span className="text-ink-3">(the specific post, creator or placement)</span></label>
            <Input value={form.utm_content} onChange={(e) => setForm({ ...form, utm_content: e.target.value })}
              placeholder="@cryptokate-reel-2" className={field} data-testid="utm-create-content" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={create} disabled={busy || !form.label.trim()} data-testid="utm-create-btn">
            {busy ? <Loader2 className="animate-spin" size={12} /> : <><Plus size={13} className="mr-1" /> Create link</>}
          </Button>
        </div>
      </div>

      {/* Links */}
      {(data.links || []).length === 0 ? (
        <p className="text-center text-xs text-ink-3 py-6" data-testid="utm-empty">
          No links yet — build your first one above.
        </p>
      ) : (
        <div className="space-y-2.5">
          {data.links.map((l) => (
            <div key={l.id} className="rounded-xl p-3.5" data-testid={`utm-link-row-${l.id}`}
              style={{ background: "white", border: "1px solid rgba(15,18,38,0.09)", opacity: l.active ? 1 : 0.6 }}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-2" style={{ color: "#0F1226" }}>
                    {l.label}
                    {!l.active && <span className="chip chip-gray text-[10px]">Paused</span>}
                    <span className="chip text-[10px]" style={{ background: "rgba(14,165,233,0.12)", color: "#0369A1" }}>{l.dest_label}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    {["utm_source", "utm_medium", "utm_campaign", "utm_content"].map((k) => l[k] && (
                      <span key={k} className="text-[10px] font-mono px-1.5 py-0.5 rounded"
                        style={{ background: "rgba(15,18,38,0.05)", color: "#5A607F" }}>
                        {k.replace("utm_", "")}: {l[k]}
                      </span>
                    ))}
                    {l.owner_email && <span className="text-[10px] text-ink-3">· {l.owner_email}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Stat n={l.clicks || 0} label="clicks" tid={`utm-clicks-${l.id}`} />
                  <Stat n={l.leads || 0} label="leads" tid={`utm-leads-count-${l.id}`} />
                  <Stat n={l.started || 0} label="started" tid={`utm-started-${l.id}`} />
                  <Stat n={l.joined || 0} label="joined" tid={`utm-joined-${l.id}`} />
                </div>
              </div>

              <div className="font-mono text-[11px] mt-2.5 break-all rounded-lg px-2.5 py-2 select-all"
                style={{ background: "#F8FAFC", border: "1px solid rgba(15,18,38,0.08)", color: "#0F1226" }}
                data-testid={`utm-url-${l.id}`}>
                {fullUrl(l)}
              </div>

              <div className="flex gap-2 mt-2.5 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => copy(fullUrl(l))} data-testid={`utm-copy-${l.id}`}>
                  <Copy size={12} className="mr-1" /> Copy
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(fullUrl(l), "_blank")} data-testid={`utm-open-${l.id}`}>
                  <ExternalLink size={12} className="mr-1" /> Test
                </Button>
                <Button size="sm" variant="outline" onClick={() => showLeads(l)} data-testid={`utm-leads-${l.id}`}>
                  <Users size={12} className="mr-1" /> {openLeads === l.id ? "Hide leads" : "Leads"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(l)} data-testid={`utm-toggle-${l.id}`}>
                  {l.active ? <><Pause size={12} className="mr-1" /> Pause</> : <><Play size={12} className="mr-1" /> Resume</>}
                </Button>
                <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(l)} data-testid={`utm-delete-${l.id}`}>
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </div>

              {openLeads === l.id && (
                <div className="mt-3 rounded-lg overflow-hidden" style={{ border: "1px solid rgba(15,18,38,0.08)" }}
                  data-testid={`utm-leads-panel-${l.id}`}>
                  {!leads[l.id] ? (
                    <div className="py-6 text-center"><Loader2 className="animate-spin mx-auto" size={16} /></div>
                  ) : leads[l.id].length === 0 ? (
                    <p className="text-xs text-ink-3 text-center py-5" data-testid={`utm-leads-empty-${l.id}`}>
                      Nobody has come through this link yet.
                    </p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: "rgba(15,18,38,0.03)" }}>
                          <th className="text-left px-2.5 py-1.5 font-semibold">Lead</th>
                          <th className="text-left px-2.5 py-1.5 font-semibold">Contact</th>
                          <th className="text-left px-2.5 py-1.5 font-semibold">Sizzle</th>
                          <th className="text-left px-2.5 py-1.5 font-semibold">Presentation</th>
                          <th className="text-left px-2.5 py-1.5 font-semibold">Where they are</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads[l.id].map((p) => (
                          <tr key={p.id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }}
                            data-testid={`utm-lead-row-${p.id}`}>
                            <td className="px-2.5 py-1.5 font-medium">{p.name || <span className="text-ink-3">anonymous</span>}</td>
                            <td className="px-2.5 py-1.5 text-ink-2">
                              {p.email || "—"}{p.phone ? <span className="block text-[10px] text-ink-3">{p.phone}</span> : null}
                            </td>
                            <td className="px-2.5 py-1.5 tabular-nums">{Math.round(p.sizzle_pct || 0)}%</td>
                            <td className="px-2.5 py-1.5 tabular-nums">{Math.round(p.present_pct || 0)}%</td>
                            <td className="px-2.5 py-1.5">{p.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
